// Angular imports
import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third-party libraries
import { BehaviorSubject, lastValueFrom } from 'rxjs';

// Project libraries
import { GqlService, socialMutations } from '@tgx/shared/data-access/graphql';
import {
  UpdateMemberDataInput,
  ProfileData,
  UpdateProfileOutput,
  UpdateUserProfileOutput,
  UserProfileOutput,
} from '@tgx/shared/interfaces';

// Local imports
import { CompanyContactsInfo } from '../interface/user-profile-contacts-info';
import { ContactInfo } from '../enum/contact-info';
import { updateUserProfileContactInfo } from '../operations/mutations/update-user-profile-contact-info';
import { getUserProfileContactInfo } from '../operations/queries/get-user-profile-contact-info';
import { GeoLocationCity, GeoLocationOutput } from '../interface/onboarding';
import { HttpClient } from '@angular/common/http';
import { PartnerType } from '@tgx/shared/enums';
import { ContactStepPrefix } from '../interface/company-contacts-step-data';
import { AdminService, URL_WEB_BFF, URL_NETWORK_SERVICE } from '@tgx/shared/services';
import { WEB_BFF_ENDPOINTS } from '@tgx/shared/constants';

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private gqlService = inject(GqlService);
  private http = inject(HttpClient);
  private adminService = inject(AdminService);

  private isEditingSubject = new BehaviorSubject<boolean>(false);
  private selectedContactTypeToEditSubject = new BehaviorSubject<ContactStepPrefix | null>(null);
  private readonly contactInfoToPrefixMap: Record<ContactInfo, ContactStepPrefix> = {
    [ContactInfo.API_IMPLEMENTATION]: 'Api',
    [ContactInfo.TECH_DECISION]: 'TechDecision',
    [ContactInfo.TECH_SUPPORT]: 'TechSupport',
    [ContactInfo.BILLING]: 'Billing',
  };

  isEditing$ = this.isEditingSubject.asObservable();
  private urlWebBff = inject(URL_WEB_BFF);
  private urlNetworkService = inject(URL_NETWORK_SERVICE);

  setIsEditing(value: boolean) {
    this.isEditingSubject.next(value);
  }

  getIsEditState(): boolean {
    return this.isEditingSubject.getValue();
  }

  setSelectedContactTypeToEdit(contactType: ContactStepPrefix | null) {
    this.selectedContactTypeToEditSubject.next(contactType);
  }

  getSelectedContactTypeToEdit(): ContactStepPrefix | null {
    return this.selectedContactTypeToEditSubject.getValue();
  }

  scrollTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  getValidationStatus(formGroup: FormGroup, controlName: string, isRequired: boolean): string {
    const controlField = formGroup.get(controlName);

    if (!controlField) {
      return '';
    }

    if (controlField.errors !== null && controlField.dirty) {
      return 'is-invalid';
    } else if (controlField.errors === null && controlField.dirty) {
      if (isRequired) {
        return controlField.value ? 'is-valid' : 'is-invalid';
      } else {
        return 'is-valid';
      }
    } else {
      return '';
    }
  }

  // change jobTitle and role at organization member level
  updateMemberDataInOrganization(email: string, orgCode: string, data: UpdateMemberDataInput): Promise<string> {
    return this.adminService.updateMemberDataInOrganization(email, orgCode, data);
  }

  updateProfessionalDetailsFinishedAt(email: string, professionalDetailsFinishedAt: string): Promise<void> {
    const body = {
      where: { email },
      data: {
        onboardingProperties: {
          professionalDetailsFinishedAt,
        },
      },
    };

    return new Promise((resolve, reject) => {
      this.gqlService
        .mutationGateway<UpdateUserProfileOutput>(socialMutations.updateUserProfile, body)
        .then((resp) => {
          const userProfileData = resp?.social?.updateUserProfile?.userProfileData;
          const adviseMessage = resp?.social?.updateUserProfile?.adviseMessage;
          const errMsg = 'There is a problem processing Social response';

          if (userProfileData) {
            resolve();
          } else if (adviseMessage?.length > 0 && adviseMessage[0]?.description) {
            reject(adviseMessage[0].description);
          } else {
            reject(errMsg);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // cambia el nombre de la org
  updateProfile(orgCode: string, name: string): Promise<ProfileData> {
    const body = { data: { name: name }, where: { ownerCode: orgCode } };
    return new Promise((resolve, reject) => {
      this.gqlService
        .mutationGateway<UpdateProfileOutput>(socialMutations.updateBuyerOnboardingDate, body)
        .then((resp) => {
          const profileData = resp?.social?.updateProfile?.profileData;
          const adviseMessage = resp?.social?.updateProfile?.adviseMessage;
          const errMsg = 'There is a problem processing Social response';

          if (profileData) {
            resolve(profileData);
          } else if (adviseMessage?.length > 0 && adviseMessage[0]?.description) {
            reject(adviseMessage[0].description);
          } else {
            reject(errMsg);
          }
        })
        .catch((err) => reject(err));
    });
  }

  getUserProfileContactInfo(email: string): Promise<CompanyContactsInfo> {
    const data = { where: { email } };
    return new Promise((resolve, reject) => {
      this.gqlService
        .queryGateway<UserProfileOutput>(getUserProfileContactInfo, data)
        .then((resp) => {
          if (resp || resp?.social) {
            const contactsInfo = resp.social?.userProfile?.userProfileData?.onboardingProperties?.contactsInfo;

            if (!contactsInfo) {
              resolve(null);
            } else {
              try {
                resolve(this.parseAndNormalizeContactsInfo(contactsInfo));
              } catch (error) {
                reject(error);
              }
            }
          } else {
            reject(resp);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  updateUserProfileContactInfo(email: string, contactData: CompanyContactsInfo): Promise<CompanyContactsInfo> {
    const jsonData = JSON.stringify(contactData, null);

    const data = { where: { email: email }, data: { onboardingProperties: { contactsInfo: jsonData } } };

    return new Promise((resolve, reject) => {
      this.gqlService
        .mutationGateway<UpdateUserProfileOutput>(updateUserProfileContactInfo, data)
        .then((resp) => {
          if (resp || resp?.social) {
            const contactsInfo = resp.social?.updateUserProfile?.userProfileData?.onboardingProperties?.contactsInfo;
            if (contactsInfo) {
              try {
                resolve(this.parseAndNormalizeContactsInfo(contactsInfo));
              } catch (error) {
                reject(error);
              }
            } else {
              reject(resp);
            }
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private parseAndNormalizeContactsInfo(contactsInfo: unknown): CompanyContactsInfo {
    if (!contactsInfo) {
      return null;
    }

    if (typeof contactsInfo === 'string') {
      return this.normalizeContactsInfo(JSON.parse(contactsInfo));
    }

    return this.normalizeContactsInfo(contactsInfo);
  }

  private normalizeContactsInfo(rawData: unknown): CompanyContactsInfo {
    if (!rawData || typeof rawData !== 'object') {
      return {};
    }

    const data = rawData as Record<string, CompanyContactsInfo[keyof CompanyContactsInfo]>;
    const normalized: CompanyContactsInfo = {};

    Object.entries(this.contactInfoToPrefixMap).forEach(([contactInfo, prefix]) => {
      const source = data[contactInfo] ?? data[prefix];

      if (!source) {
        return;
      }

      normalized[contactInfo as ContactInfo] = source;
      normalized[prefix] = source;
    });

    return normalized;
  }

  getCitiesByCountryISO(countryISO: string): Promise<GeoLocationCity[]> {
    const route = WEB_BFF_ENDPOINTS.GET_CITIES_BY_COUNTRY.replace(':countryCode', encodeURIComponent(countryISO));
    const apiUrl = `${this.urlWebBff}${route}`;

    return lastValueFrom(this.http.get<GeoLocationOutput>(apiUrl))
      .then((response) => {
        const cities = Array.isArray(response?.geonames) ? response.geonames : [];

        if (!cities.length) {
          throw new Error('Invalid response from cities BFF endpoint');
        }

        return cities.sort((a, b) => a.name.localeCompare(b.name));
      })
      .catch((error) => {
        console.error('Error fetching cities from BFF:', error);
        throw error;
      });
  }

  async finishHubspotRegistration(crmID: string, partnerType: PartnerType[]): Promise<boolean> {
    const properties = [
      ...(partnerType.includes(PartnerType.BUYER) ? [{ key: 'buyer_registration_finished', value: 'true' }] : []),
      ...(partnerType.includes(PartnerType.SELLER) ? [{ key: 'seller_registration_finished', value: 'true' }] : []),
    ];

    const body = {
      companyCrmId: crmID,
      properties,
    };

    await this.http.post(`${this.urlNetworkService}crm/update-company`, body).toPromise();
    return true;
  }
}
